import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FileCheck2, Pencil, Send, Check, MessageSquare, CalendarClock, Image as ImageIcon, RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, RunButton, BackLink, SelectField, useLoad, fmt, label, TZ_OPTIONS,
} from "@/components/control/kit";
import { contentFields } from "@/pages/Content";

const YESNO = [{ value: "ja", label: "Ja" }, { value: "nein", label: "Nein" }];
const CHECKS = [["sachlich", "Sachlich richtig"], ["verstaendlich", "Leicht verständlich"], ["bildrechte", "Bildrechte geklärt"], ["alternativtext", "Alternativtext vorhanden"]];

function Actions({ c, can, blocked, reload }) {
  const base = `/content/${c.id}`;
  return (
    <>
      <Status value={c.status} testId="content-status" />
      {["entwurf", "rückfrage"].includes(c.status) && can.manage && (
        <ActionButton label="Bearbeiten" icon={Pencil} testId="edit-content" fields={contentFields(c)} onDone={reload} submitLabel="Neue Version speichern"
          page="bearbeiten" title="Inhalt bearbeiten" pageSubtitle="Bearbeite einen verständlichen Artikel für die gewählte Zielgruppe."
          onSubmit={async (v) => { const { data } = await api.patch(base, v); return `Version ${data.version} gespeichert.`; }} />
      )}
      {c.status === "entwurf" && can.manage && (
        <RunButton label="Zur Freigabe senden" icon={Send} variant="default" testId="submit-content" success="Zur Freigabe gesendet." onDone={reload} run={() => api.post(`${base}/submit`)} />
      )}
      {c.status === "in_pruefung" && can.approve && blocked && (
        <p className="max-w-md rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note" data-testid="content-review-blocked">{blocked}</p>
      )}
      {c.status === "in_pruefung" && can.approve && !blocked && (
        <>
          <ActionButton label="Freigeben" icon={Check} variant="default" testId="approve-content" onDone={reload} title="Redaktionelle Freigabe" submitLabel="Freigabe bestätigen"
            page="freigabe" pageSubtitle="Prüfe Inhalt, Verständlichkeit und Bildverwendung getrennt."
            description="Die Freigabe muss durch eine andere Person als die Autorin oder der Autor erfolgen."
            fields={CHECKS.map(([name, label]) => ({ name, label, type: "select", options: YESNO, required: true }))}
            onSubmit={async (v) => { await api.post(`${base}/review`, { action: "approve", checks: v }); return "Inhalt freigegeben."; }} />
          <ActionButton label="Rückfrage" icon={MessageSquare} testId="ask-content" onDone={reload} submitLabel="Rückfrage senden"
            page="rueckfrage" title="Rückfrage zum Inhalt" pageSubtitle="Die Rückfrage geht an die Autorin oder den Autor des Inhalts."
            fields={[{ name: "message", label: "Rückfrage", type: "textarea", required: true }]}
            onSubmit={async (v) => { await api.post(`${base}/review`, { action: "ask", ...v }); return "Rückfrage gespeichert."; }} />
        </>
      )}
      {c.status === "freigegeben" && can.approve && (
        <ActionButton label="Veröffentlichung planen" icon={CalendarClock} testId="schedule-content" onDone={reload} submitLabel="Planung bestätigen"
          page="planen" title="Veröffentlichung planen" pageSubtitle="Die Veröffentlichung wird nach Freigabe und Bestätigung geplant."
          description="Liegt der Zeitpunkt in der Vergangenheit, wird sofort veröffentlicht."
          fields={[
            { name: "publish_at", label: "Datum und Uhrzeit", type: "datetime-local", required: true },
            { name: "timezone", label: "Zeitzone", type: "select", options: TZ_OPTIONS, required: true, default: "Europe/Zurich", placeholder: false },
          ]}
          onSubmit={async (v) => { const { data } = await api.post(`${base}/schedule`, v); return data.status === "veröffentlicht" ? "Inhalt veröffentlicht." : "Veröffentlichung geplant."; }} />
      )}
    </>
  );
}

const reject = (detail) => Object.assign(new Error(detail), { response: { status: 400, data: { detail } } });

async function saveMedia(id, v) {
  if (v.file && v.url.trim()) throw reject("Bitte entweder ein Bild hochladen oder eine Bildadresse angeben.");
  if (!v.file) {
    await api.post(`/content/${id}/media`, { url: v.url, alt: v.alt, credit: v.credit, consent_checked: v.consent_checked });
    return "Bildangaben gespeichert.";
  }
  const form = new FormData();
  form.append("file", v.file);
  form.append("alt", v.alt);
  form.append("credit", v.credit || "");
  form.append("consent_checked", v.consent_checked ? "true" : "false");
  await api.post(`/content/${id}/media/upload`, form);
  return "Bild hochgeladen.";
}

function MediaPreview({ c }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const fileId = c.media?.file_id;
  useEffect(() => {
    if (!fileId) { return undefined; }
    let url = null;
    let alive = true;
    api.get(`/content/${c.id}/media/file`, { responseType: "blob" })
      .then((r) => { if (alive) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [c.id, fileId]);
  if (failed) return <p className="text-sm text-amber-800" data-testid="content-media-error">Das Bild konnte nicht geladen werden.</p>;
  return src ? <img src={src} alt={c.media.alt} className="max-h-48 rounded-lg object-cover" data-testid="content-media-image" /> : null;
}

function mediaFile(m) {
  return `${m.mime.split("/")[1].toUpperCase()} · ${Math.max(1, Math.round(m.size / 1024))} KB · ${m.width}×${m.height} px`;
}

function Media({ c, can, reload }) {
  const m = c.media || {};
  return (
    <Section title="Medien und Bildbeschreibung" testId="content-media" actions={can.manage && !["geplant", "veröffentlicht"].includes(c.status) && (
      <ActionButton label="Bild bearbeiten" icon={ImageIcon} testId="edit-media" onDone={reload} submitLabel="Beschreibung speichern"
        page="medien" title="Medien und Bildbeschreibung" pageSubtitle="Prüfe Beschreibung und Nutzungsberechtigung des Beitragsbilds."
        pageSubject={c.title} pageTop={m.file_id ? <MediaPreview c={c} /> : null}
        pageRows={[["Bild", m.file_id ? mediaFile(m) : m.url ? "verlinkte Adresse (kein Upload)" : "noch keines"],
          ["Bildbeschreibung", m.alt || "fehlt"], ["Nutzungsrecht", m.consent_checked ? "Einwilligung liegt vor" : "Vor Veröffentlichung klären"],
          ["Status", label(c.status)]]}
        description="Hochgeladene Bilder liegen in der bestehenden Datenbank und sind nur im Control OS sichtbar. Eine Auslieferung an die Mitglieder-App ist noch nicht eingerichtet."
        fields={[
          { name: "file", label: "Bild hochladen (PNG, JPEG oder WebP, höchstens 2 MB)", type: "file", accept: "image/png,image/jpeg,image/webp",
            help: m.file_id ? "Ersetzt das bisher hochgeladene Bild. Der Dateiname wird nicht übernommen." : "Der Dateiname wird nicht übernommen; das Bild erhält einen zufälligen Namen." },
          { name: "url", label: "Oder Bildadresse (https://…)", type: "url", default: m.url || "", help: "Eine Adresse wird nur verlinkt, nicht hochgeladen." },
          { name: "alt", label: "Alternativtext", type: "textarea", required: true, default: m.alt || "" },
          { name: "credit", label: "Bildnachweis", default: m.credit || "" },
          { name: "consent_checked", label: "Einwilligung abgebildeter Personen liegt vor", type: "checkbox", default: !!m.consent_checked },
        ]}
        onSubmit={(v) => saveMedia(c.id, v)} />
    )}>
      {!c.media ? <Empty text="Kein Bild hinterlegt." /> : (
        <div className="space-y-3">
          {m.file_id ? <MediaPreview c={c} /> : m.url && <img src={m.url} alt={m.alt} className="max-h-48 rounded-lg object-cover" />}
          <Info items={[m.file_id && ["Datei", mediaFile(m)], m.file_id && ["Speicher", "Intern, nur im Control OS sichtbar"],
            !m.file_id && m.url && ["Bildadresse", "verlinkt, nicht hochgeladen"],
            ["Alternativtext", m.alt], ["Bildnachweis", m.credit], ["Einwilligung", m.consent_checked ? "liegt vor" : "nicht bestätigt"]]} />
        </div>
      )}
    </Section>
  );
}

function Versions({ c, can, reload }) {
  const versions = c.versions || [];
  const last = versions.length;
  const [a, setA] = useState(String(Math.max(1, last - 1)));
  const [b, setB] = useState(String(last));
  const va = versions.find((v) => String(v.n) === a) || {};
  const vb = versions.find((v) => String(v.n) === b) || {};
  const opts = versions.map((v) => ({ value: String(v.n), label: `Version ${v.n} · ${fmt(v.at)}` }));
  return (
    <Section title="Versionen vergleichen" testId="content-versions">
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <SelectField id="ver-a" label="Version A" value={a} onChange={setA} options={opts} testId="version-a" />
        <SelectField id="ver-b" label="Version B" value={b} onChange={setB} options={opts} testId="version-b" />
      </div>
      {["title", "summary", "body"].map((k) => (
        <div key={k} className="mb-3 grid gap-3 sm:grid-cols-2">
          {[va, vb].map((v, i) => (
            <p key={i} className={`whitespace-pre-wrap rounded-lg border p-2 text-sm ${va[k] !== vb[k] ? "border-orange-300 bg-orange-50" : "border-border"}`}>
              <span className="block text-xs font-medium text-muted-foreground">{{ title: "Titel", summary: "Kurztext", body: "Text" }[k]}</span>{v[k] || "–"}
            </p>
          ))}
        </div>
      ))}
      {can.manage && va.n && va.n !== last && !["geplant", "in_pruefung"].includes(c.status) && (
        <RunButton label={`Version ${va.n} als neuen Entwurf übernehmen`} icon={RotateCcw} testId="restore-version" success="Version als neuer Entwurf übernommen."
          onDone={reload} run={() => api.post(`/content/${c.id}/restore`, { version: va.n })} />
      )}
    </Section>
  );
}

function ContentView({ c, can, blocked, reload }) {
  return (
    <>
      <PageHeader title={c.title} subtitle={`${c.audience === "öffentlich" ? "Öffentlich" : "Mitglieder-App"} · ${c.author?.name || ""}`} icon={FileCheck2}
        actions={<Actions c={c} can={can} blocked={blocked} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Inhalt" testId="content-body">
          <Info items={[["Kurztext", c.summary], ["Freigegeben durch", c.approved_by?.name],
            c.publish_at && ["Veröffentlichung", `${fmt(c.publish_at)} (${c.timezone})`], c.review_note && ["Rückfrage", c.review_note]]} />
          <p className="mt-4 whitespace-pre-wrap text-sm text-zinc-800">{c.body}</p>
        </Section>
        <Media c={c} can={can} reload={reload} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Versions c={c} can={can} reload={reload} />
        <Section title="Verlauf" testId="content-history"><History items={c.history} /></Section>
      </div>
    </>
  );
}

export default function ContentDetail() {
  const { id } = useParams();
  const st = useLoad(`/content/${id}`);
  const list = useLoad("/content?status=entwurf");
  const can = list.data?.can || { manage: false, approve: false };
  return (
    <div>
      <BackLink to="/inhalte-freigaben">Zurück zu Inhalte & Freigaben</BackLink>
      <Loadable state={st}>{(d) => <ContentView key={d.item.versions?.length} c={d.item} can={can} blocked={d.review_blocked_reason} reload={st.reload} />}</Loadable>
    </div>
  );
}
